import { createWebHistory, createRouter } from "vue-router";
import { getGlobalStore } from "./AppStore.js";
import filters from '../common/filters.js';

// 관리자
import CommonSelectList from "./adm/boardManagement/template/commonTemplate/CommonSelectList.vue";
import CommonSelectListOne from "./adm/boardManagement/template/commonTemplate/CommonSelectListOne.vue";

// NotFound
import NotFound from "./user/etc/NotFound.vue";
import AccessDeniedComp from "./user/etc/AccessDenied.vue";
import SSOCheck from "./login/SSOCheck.vue";
import SSOLogin from "./login/SSOLogin.vue";
import SSOPropagate from "./login/SSOPropagate.vue";

const beforeRoutes = [
	{ path: '/:pathMatch(.*)*', name: "PageNotFound", component: NotFound },
	{ path: '/accessDenied.page', name: "AccessDeniedPage", component: AccessDeniedComp },
	{ path: '/ssoCheck.page', name: "SSOCheck", component: SSOCheck },
	{ path: '/ssoLogin.page', name: "SSOLogin", component: SSOLogin },
	{ path: '/ssoPropagate.page', name: "SSOPropagate", component: SSOPropagate },

	// 관리자
	{
		path: "/adm",
		children: [
			{ path: "commonSelectListOne.page", name: "CommonSelectListOne", korName: "", component: CommonSelectListOne },
			{ path: "commonSelectList.page", name: "CommonSelectList", korName: "", component: CommonSelectList },
		],
	},
];

import { findAll, accessCheck } from "../../resources/api/router.js";
import { getCntxtPth } from "../../resources/api/cntxtPth.js";
import { save } from "../../resources/api/cntnStats.js";
import { getThemeApiCache } from '../index.js';

// Context Path 정보 호출
async function getContextPath() {
	try {
		const store = getGlobalStore();
		const res = await getCntxtPth();
		if (res.status == 200) {
			let ctx = res.data.data || '';
			if (ctx == '/') {
				ctx = '';
			}
			store.commit("setContextPath", ctx);
			return ctx;
		} else {
			store.commit("setStoreReset");
			window.location = filters.ctxPath('/')
		}
	} catch (error) {
		return [];
	}
}

// 라우터 정보 호출
async function fetchRoutes() {
	try {
		const store = getGlobalStore();
		const cache = getThemeApiCache();

		const res = await findAll();
		if (res.status == 200) {
			const newRoutes = res.data.data.map(route => {
				const componentPath = resolveComponentPath(route, cache);
				return createRoute(route, componentPath);
			});
			return newRoutes;
		} else {
			store.commit("setStoreReset");
			window.location = filters.ctxPath('/')
		}
	} catch (error) {
		console.error('라우트 fetch 오류:', error);
		return [];
	}
}

// 게시판 컴포넌트 경로 결정 (3단계 fallback: 메인 테마 → 기본 테마 → 원본 경로)
function resolveBbsComponentPath(route, themeCache) {
	try {
		// 게시판 라우트가 아니거나 관리자 경로면 원본 경로 반환
		if (!route.contsId?.includes('BBS_MNG') || route.userPagePath?.includes('/adm/')) {
			return route.compnPath;
		}

		const { mainTheme, defaultTheme } = themeCache || {};

		// 원본 경로에서 게시판 타입과 파일명 추출
		const pathParts = route.compnPath.split('/');
		const fileName = pathParts.pop();
		const templateFolder = pathParts.pop();
		const boardType = templateFolder.replace('Template', '');
		const targetPath = `/bbs/${boardType}/${fileName}`;

		// 메인 테마에서 찾기
		if (mainTheme?.themaFiles?.some(f => f.rltPath === targetPath)) {
			return `./user/themes/${mainTheme.themaNmEng}${targetPath}`;
		}

		// 기본 테마에서 찾기
		if (defaultTheme?.themaFiles?.some(f => f.rltPath === targetPath)) {
			return `./user/themes/${defaultTheme.themaNmEng}${targetPath}`;
		}

		// 원본 경로 사용
		return route.compnPath;
	} catch (error) {
		console.error('BBS 경로 처리 중 에러:', error, route);
		return route.compnPath;
	}
}

// portal 공용 페이지였던 컴포넌트의 경로 결정 (3단계 fallback: 메인 테마 → 기본 테마 → 원본 경로)
// portal/common(StaticPageRenderer)은 여전히 테마 공용이므로 대상에서 제외
function resolvePortalComponentPath(route, themeCache) {
	try {
		const portalPrefix = './user/portal/';
		if (route.userPagePath?.includes('/adm/') ||
			!route.compnPath?.startsWith(portalPrefix) ||
			route.compnPath.startsWith(`${portalPrefix}common/`)) {
			return route.compnPath;
		}

		const { mainTheme, defaultTheme } = themeCache || {};

		// portal 기준 파일명 추출 (테마 pages 폴더는 서브폴더 없이 평탄 구조이므로 파일명만 사용, ex. MyInfoMain.vue)
		const fileName = route.compnPath.split('/').pop();

		// 메인 테마에서 찾기
		if (mainTheme?.themaFiles?.some(f => f.themaCl === 'pages' && f.fileNm === fileName)) {
			return `./user/themes/${mainTheme.themaNmEng}/pages/${fileName}`;
		}

		// 기본 테마에서 찾기
		if (defaultTheme?.themaFiles?.some(f => f.themaCl === 'pages' && f.fileNm === fileName)) {
			return `./user/themes/${defaultTheme.themaNmEng}/pages/${fileName}`;
		}

		// 원본 경로 사용 (더 이상 존재하지 않을 수 있음 → createRoute의 NotFound fallback으로 처리됨)
		return route.compnPath;
	} catch (error) {
		console.error('portal 경로 처리 중 에러:', error, route);
		return route.compnPath;
	}
}

// BBS, portal 각각의 테마 경로 해석을 순서대로 적용
function resolveComponentPath(route, themeCache) {
	const bbsResolvedPath = resolveBbsComponentPath(route, themeCache);
	if (bbsResolvedPath !== route.compnPath) {
		return bbsResolvedPath;
	}
	return resolvePortalComponentPath(route, themeCache);
}

// 라우트 객체 생성
function createRoute(route, componentPath) {
	return {
		path: route.userPagePath,
		name: route.contsEngNm,
		korName: route.contsKornNm,
		component: () => import(`${componentPath}`)
			.catch(async err => {
				// import 실패 시 원본 경로 재시도
				if (componentPath !== route.compnPath) {
					try {
						return await import(`${route.compnPath}`);
					} catch {
						return await import(`./user/etc/NotFound.vue`);
					}
				}
				return import(`./user/etc/NotFound.vue`);
			}),
		meta: {
			authrt: route.authrtList,
			typeId: route.contsId,
			korName: route.contsKornNm,
			isBbsRoute: route.contsId?.includes('BBS_MNG')
		}
	};
}

// Context Path를 라우터에 추가하는 함수
function addContextPathToRoutes(routes, contextPath) {
	return routes.map(route => {
		const newRoute = {
			...route,
			path: contextPath + route.path
		};
		return newRoute;
	});
}

// 접근 제어 확인
async function accessUrl(path) {
	try {
		const res = await accessCheck({
			path: path
		});
		if (res.status == 200) {
			return res.data.data;
		}
	} catch (error) {
		return false;
	}
}

// 사용자 권한과 라우터에 포함되어 있는 권한을 비교하여 최종 권한 추출
function mergeAuth(userRoles, to) {
	const isAdminAuthrt = userRoles.includes('ROLE_ADMIN'); // 사용자에게 ROLE_ADMIN 권한이 있는지 확인
	const isAdminRoute = to.path.startsWith(filters.ctxPath('/adm')); // 관리자 경로 여부 확인
	const isAdmAccess = isAdminAuthrt && isAdminRoute; // 관리자가 관리자 페이지에 접근하는지 여부
	const routeMeta = to.meta;
	const result = {
		inqAuthrt: 'N',
		regAuthrt: 'N',
		mdfcnAuthrt: 'N',
		delAuthrt: 'N',
		fileDwnldAuthrt: 'N',
		manageAuthrt: 'N',
	};

	if (!routeMeta || !routeMeta.authrt || routeMeta.authrt.length === 0) {
		return result;
	}

	routeMeta.authrt.forEach(auth => {
		if (userRoles.includes(auth.authrtCd)) {
			// 관리자&관리자 경로라면 auth.menuUser가 'A'인 권한만 적용, 그 외에는 auth.menuUser가 'U'이거나 없는 권한 적용
			const isValid = isAdmAccess
				? (auth.menuUser === 'A')
				: (auth.menuUser === 'U' || !auth.menuUser);

			// 조건이 맞을 때만 권한 적용
			if (isValid) {
				if (auth.inqAuthrt === 'Y') result.inqAuthrt = 'Y';
				if (auth.regAuthrt === 'Y') result.regAuthrt = 'Y';
				if (auth.mdfcnAuthrt === 'Y') result.mdfcnAuthrt = 'Y';
				if (auth.delAuthrt === 'Y') result.delAuthrt = 'Y';
				if (auth.fileDwnldAuthrt === 'Y') result.fileDwnldAuthrt = 'Y';
				// if (auth.manageAuthrt === 'Y') result.manageAuthrt = 'Y'; (임시 미사용 처리)
			}
		}
	});
	return result;
}

// 통계 등록
async function cntnStatsSave(typeId, roles) {
	try {
		const cntnStats = {
			'typeId': typeId,
			'mbrAuthList': roles
		}
		await save(cntnStats);
	} catch (error) {
	}
}

// 권한 검증
function isValidRole() {
	const store = getGlobalStore();
	const roles = store.state.roles;
	if (!Array.isArray(roles)) {
		store.commit("setStoreReset");
		return false;
	}
	for (const role of roles) {
		if (typeof role !== 'object' || !role.hasOwnProperty('authority')) {
			store.commit("setStoreReset");
			return false;
		}
	}
	return true;
}

// 테마 pages 동적 라우팅 생성 (메인 테마 우선, 기본 테마로 fallback)
function createThemePageRoutes() {
	try {
		let pageRouteMap = new Map();

		const cache = getThemeApiCache();
		const defaultTheme = cache.defaultTheme;
		const mainTheme = cache.mainTheme;

		// 기본 테마 페이지 추가
		if (defaultTheme?.themaFiles) {
			const defaultPageFiles = defaultTheme.themaFiles.filter(f => f.themaCl === 'pages');

			defaultPageFiles.forEach(file => {
				const pageName = file.fileNm.replace('.vue', '');
				const routePath = `/${pageName.toLowerCase()}.page`;

				pageRouteMap.set(routePath, {
					path: routePath,
					name: `ThemePage_${pageName}`,
					korName: pageName,
					component: () => import(`./user/themes/${defaultTheme.themaNmEng}/pages/${file.fileNm}`)
						.catch(async () => {
							console.error(`기본 테마 페이지 로드 실패: ${file.fileNm}`);
							try {
								return await import(`./user/themes/default/pages/${file.fileNm}`);
							} catch {
								return await import(`./user/etc/NotFound.vue`);
							}
						}),
					meta: {
						isThemePage: true,
						themeName: defaultTheme.themaNmEng,
						fileName: file.fileNm
					}
				});
			});
		}

		// 메인 테마로 덮어쓰기
		if (mainTheme?.themaFiles) {
			const mainPageFiles = mainTheme.themaFiles.filter(f => f.themaCl === 'pages');

			mainPageFiles.forEach(file => {
				const pageName = file.fileNm.replace('.vue', '');
				const routePath = `/${pageName.toLowerCase()}.page`;

				pageRouteMap.set(routePath, {
					path: routePath,
					name: `ThemePage_${pageName}`,
					korName: pageName,
					component: () => import(`./user/themes/${mainTheme.themaNmEng}/pages/${file.fileNm}`)
						.catch(async () => {
							console.error(`메인 테마 페이지 로드 실패: ${file.fileNm}`);
							try {
								return await import(`./user/themes/default/pages/${file.fileNm}`);
							} catch {
								return await import(`./user/etc/NotFound.vue`);
							}
						}),
					meta: {
						isThemePage: true,
						themeName: mainTheme.themaNmEng,
						fileName: file.fileNm
					}
				});
			});
		}

		// Main.vue를 "/"로 연결
		const mainRoute = pageRouteMap.get('/main.page');
		if (mainRoute) {
			pageRouteMap.set('/', {
				...mainRoute,
				path: '/',
				name: 'main',
				korName: '홈'
			});
		}

		return Array.from(pageRouteMap.values());
	} catch (error) {
		console.error('테마 페이지 라우트 생성 실패:', error);
		return [];
	}
}

export default async function createAppRouter() {
	const ctx = await getContextPath();
	const dynamicRoutes = await fetchRoutes();
	const themePageRoutes = createThemePageRoutes();

	const prefixedBeforeRoutes = addContextPathToRoutes(beforeRoutes, ctx);
	const prefixedDynamicRoutes = addContextPathToRoutes(dynamicRoutes, ctx);
	const prefixedThemePageRoutes = addContextPathToRoutes(themePageRoutes, ctx);

	const newRoutes = prefixedThemePageRoutes
		.concat(prefixedBeforeRoutes)
		.concat(prefixedDynamicRoutes);

	const AppRouter = createRouter({
		history: createWebHistory(),
		routes: newRoutes,

		// 라우팅 시 스크롤 위치는 항상 최상단으로 이동, 뒤로가기 시 이전 스크롤 위치로 이동
		scrollBehavior(to, from, savedPosition) {
			// 브라우저 뒤로가기/앞으로가기
			if (savedPosition) {
				// 브라우저 뒤로가기 및 앞으로가기 시, 이전 스크롤 위치로 이동
				return savedPosition;
			}

			// 일반 라우팅 시 최상단으로 이동
			return {
				top: 0,
				left: 0,
			};
		},
	});

	AppRouter.beforeEach(async (to, from, next) => {
		const store = getGlobalStore();
		const contextPath = store.state.contextPath;
		const admPath = to.path.includes('/adm');

		if (to.name === 'PageNotFound' || to.name === 'AccessDeniedPage') {
			next();
			return;
		}

		// SSO 체크 경로는 예외 처리
		if (to.path.includes('/ssoCheck.page') || to.path.includes('/ssoLogin.page') || to.path.includes('/ssoPropagate.page')) {
			next();
			return;
		}

		// 로그인 모드 확인
		let loginMode = store.state.loginMode;

		if (!loginMode || loginMode === 'undefined') {
			loginMode = localStorage.getItem('loginMode') || sessionStorage.getItem('loginMode');
		}
		if (!loginMode || loginMode === 'undefined') {
			loginMode = 'J';
		}
		if (store.state.loginMode !== loginMode) {
			store.commit('setLoginMode', loginMode);
		}

		// 로그인 상태 확인
		let isLogin = false;
		if (loginMode === 'J') {
			const token = store.state.authorization || localStorage.getItem('authorization');
			isLogin = !!token;
		} else if (loginMode === 'S') {
			const mbrId = store.state.mbrId || localStorage.getItem('mbrId') || sessionStorage.getItem('mbrId');
			const mbrNm = store.state.mbrNm || localStorage.getItem('mbrNm') || sessionStorage.getItem('mbrNm');
			isLogin = !!(mbrId || mbrNm);
		}

		// OAuth2 콜백 처리
		if (to.path.includes('/login.page') && (to.query.oauth_success || to.query.error)) {
			next();
			return;
		}

		// 이미 로그인된 상태에서 로그인 페이지로 오면 메인으로 이동
		if (isLogin) {
			if (to.path === filters.ctxPath('/cmslogin.page')) {
				next({ path: filters.ctxPath('/adm/main.page') });
				return;
			}
			if (to.path === filters.ctxPath('/login.page')) {
				next({ path: filters.ctxPath('/') });
				return;
			}
		}

		// 접근 제어 확인
		const accesCheck = await accessUrl(to.path);
		const roleCheck = isValidRole();
		if (!accesCheck || !roleCheck) {
			alert('접근이 불가합니다.\n관리자에게 문의하세요.');
			next("/accessDenied.page");
			return;
		}

		// 경로에 따른 사용자 타입 설정
		if (to.path === filters.ctxPath('/')) {
			store.commit('setUserType', 'portal')
		} else if (to.path.startsWith(filters.ctxPath('/adm'))) {
			store.commit('setUserType', 'adm');
		}

		if (to.path === filters.ctxPath('/login.page')) {
			store.commit('setPath', to.path);
			next();
			return;
		}

		const mbrAuth = store.state.roles.map(auth => auth.authority);
		const pageAuth = mergeAuth(mbrAuth, to);
		sessionStorage.setItem("redirect", to.fullPath);

		if (to.path === filters.ctxPath('/') || to.path.includes('/login.page') || to.path.includes('/cmslogin.page') || to.path.startsWith(filters.ctxPath('/cmmn/')) || to.path.includes('/signUp/') || to.path.includes('/term/view.page')) {
			// 권한과 상관없이 보여지는 페이지인 경우
			let path = to.path;
			if (to.path.includes('BBS_MNG')) {
				let logicalPath = to.path;
				if (contextPath !== '/' && logicalPath.startsWith(contextPath)) {
					logicalPath = logicalPath.substring(contextPath.length);
				}
				const lastSlashIndex = logicalPath.lastIndexOf('/');
				path = logicalPath.substring(0, lastSlashIndex);
			}
			store.commit('setPath', path);
			store.commit('setPageAuth', pageAuth);
			if (to.path === filters.ctxPath('/') || path.includes('/main.page')) {
				await cntnStatsSave(null, mbrAuth);
			}
			next();
		} else {
			// 관리자 메인 페이지는 ROLE_ADMIN이면 통과
			if (to.path.includes('/adm/main.page') && mbrAuth.includes('ROLE_ADMIN')) {
				store.commit('setPath', to.path);
				store.commit('setPageAuth', pageAuth);
				await cntnStatsSave(null, mbrAuth);
				next();
				return;
			}
			// 권한과 상관없이 보여지는 페이지가 아닌 경우 (= 권한 확인 필요)
			const hasAcc = to.matched.some(record => {
				if (!record.meta.authrt) return false;
				return record.meta.authrt.some(auth => {
					if (to.path.includes('/list.page')) {
						return mbrAuth.includes(auth.authrtCd) && auth.inqAuthrt === 'Y';
					} else if (to.path.includes('/insert.page')) {
						return mbrAuth.includes(auth.authrtCd) && auth.regAuthrt === 'Y';
					} else if (to.path.includes('/view.page')) {
						return mbrAuth.includes(auth.authrtCd) && auth.inqAuthrt === 'Y';
					} else if (to.path.includes('/main.page')) {
						return mbrAuth.includes(auth.authrtCd) && auth.inqAuthrt === 'Y';
					} else if (to.path.includes('/search.page')) {
						return mbrAuth.includes(auth.authrtCd) && auth.inqAuthrt === 'Y';
					} else {
						return false;
					}
				});
			});

			if (hasAcc) {
				if (to.path.includes('.page')) {
					let logicalPath = to.path;
					if (contextPath !== '/' && logicalPath.startsWith(contextPath)) {
						logicalPath = logicalPath.substring(contextPath.length);
					}
					const lastSlashIndex = logicalPath.lastIndexOf('/');
					const path = logicalPath.substring(0, lastSlashIndex);
					store.commit('setPath', path);
				}
				if (to.path.includes('/main.page')) {
					await cntnStatsSave(null, mbrAuth);
				} else {
					if (!to.meta.typeId.includes('BBS_MNG') || to.path.includes('/list.page')) {
						await cntnStatsSave(to.meta.typeId, mbrAuth);
					}
				}
				store.commit('setPageAuth', pageAuth);
				next();
			} else {
				if (isLogin) {
					alert('접근 권한이 없습니다.');
					next("/accessDenied.page");
				} else {
					if (admPath) {
						sessionStorage.setItem("redirect", to.fullPath);
						next({ path: filters.ctxPath("/cmslogin.page") });
					} else {
						sessionStorage.setItem("redirect", to.fullPath);
						next({ path: filters.ctxPath("/login.page") });
					}
				}
			}
		}
	});

	return AppRouter;
}